import React, { useState, useEffect } from 'react';
import { JRCModalLayout } from 'components/JRCModal/JRCModalLayout';
import { JRCImg } from 'components/JRCImg/JRCImg';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useForm, useWatch } from 'react-hook-form';

import { JRCInputCheckbox } from 'components/Form/Input/JRCInputCheckbox/JRCInputCheckbox';
import { JRCModalContent } from '../JRCModal/JRCModalContent';

const JRCFileFrame = ({ src, title }: { src: string; title: string }) => {
    const intl = useIntl();
    return (
        <iframe
            title={`${intl.formatMessage({ id: 'GLOBAL_File' })} : ${title}`}
            style={{ width: '100%', display: 'flex', flex: 1, height: '100%' }}
            src={src}></iframe>
    );
};

const JRCFileUnknown = () => {
    const intl = useIntl();
    return <>{intl.formatMessage({ id: 'GLOBAL_Unsuported_File_Format' })}</>;
};

const getFileExtension = (file: any) => {
    const mimtype = file.mimetype.toLowerCase();
    switch (mimtype) {
        default:
            return file._extend && file._extend.fileInfo ? file._extend.fileInfo.extension : 'na';
        case 'image/jpg':
        case 'image/jpeg':
        case 'image/png':
        case 'image/gif':
            return 'image';
    }
};

const Form = styled.div`
    & > fieldset {
        margin: 0px;
    }

    label {
        margin: 0px;
    }

    display: flex;
    justify-content: center;
    align-items: center;
`;

const FileActionWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 8px;
`;

const FileOpenWrapper = styled.div`
    display: flex;
    flex: 1;
`;

const FlexRow = styled.div`
    display: flex;
    flex-direction: row;
`;

const FILE_MODE_VIEW = 'view';
const FILE_MODE_EDIT = 'edit';

const checkAccess = (file: any, property: string): boolean => {
    return file && file._right && file._right[property] !== undefined ? file._right[property] : false;
};

const FormValues = ({
    control,
    name,
    onChange,
}: {
    control: any;
    name: string;
    onChange: (values: string | Array<any>) => void;
}) => {
    const values = useWatch({ control, name });

    onChange(values);

    return <></>;
};

export interface JRCFileOpenProps {
    children: React.ReactNode;
    file: any;
    fileActions?: any;
    title: string;
}

export const JRCFileOpen = (props: JRCFileOpenProps) => {
    const [open, setOpen] = useState<boolean>(false);
    const [children, setChildren] = useState<any>(null);
    const [mode, setMode] = useState<string>(FILE_MODE_VIEW);
    const idFile = props.file.id;
    const location = window.location;
    const urlBase = `${location.protocol}//${location.host}`;
    const ACCESS_UPDATE = checkAccess(props.file, 'update');

    const fopen = function () {
        const extension = getFileExtension(props.file);

        setChildren(getChildren(extension));
        setOpen(true);
    };

    const getChildren = (extension: string) => {
        switch (extension) {
            case 'pdf':
                return (
                    <JRCFileFrame
                        title={props.file.title}
                        src={`${urlBase}/downloadFile.php?id=${idFile}&attachment=no`}></JRCFileFrame>
                );
            case 'odt':
            case 'ods':
            case 'pptx':
            case 'docx':
            case 'xlsx':
                return (
                    <JRCFileFrame
                        title={props.file.title}
                        src={`${urlBase}/?action=OnlyOfficeReact&idFile=${idFile}&mode=${mode}`}></JRCFileFrame>
                );
            case 'image':
                return <JRCImg size="fitx1920" type={props.file.type} recordId={props.file.id} alt="image" />;
            default:
                return <JRCFileUnknown></JRCFileUnknown>;
        }
    };

    const intl = useIntl();

    const args: any = {
        name: 'checkbox',
        rules: {
            required: false,
        },
        options: [{ label: intl.formatMessage({ id: 'GLOBAL_Edit_Online' }), value: 'edit' }],
        direction: 'row',
        labelPosition: 'before',
        checkboxMode: 'toggle',
        variant: 'default',
    };

    const { control } = useForm({
        defaultValues: {
            ['checkbox']: [],
        },
        criteriaMode: 'all',
    });

    const modalOptions = (
        <FlexRow>
            {ACCESS_UPDATE && (
                <>
                    <Form>
                        <JRCInputCheckbox {...args} control={control as any} />
                        <FormValues
                            name="checkbox"
                            control={control}
                            onChange={(values: any) => {
                                if (values && Array.isArray(values) && values.length) {
                                    setMode(FILE_MODE_EDIT);
                                } else {
                                    setMode(FILE_MODE_VIEW);
                                }
                            }}></FormValues>
                    </Form>
                </>
            )}
            <FileActionWrapper>{props.fileActions}</FileActionWrapper>
        </FlexRow>
    );

    useEffect(() => {
        const extension = getFileExtension(props.file);
        setChildren(getChildren(extension));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    return (
        <>
            <FileOpenWrapper onClick={() => fopen()}>{props.children}</FileOpenWrapper>
            <JRCModalLayout variant="full-width" open={open} closeHandler={() => setOpen(false)}>
                <JRCModalContent
                    onClickIconClose={() => setOpen(false)}
                    title={props.title}
                    options={modalOptions}
                    isFull>
                    {children}
                </JRCModalContent>
            </JRCModalLayout>
        </>
    );
};
